// Routemaps.js
// The absolute source of truth for custom multi-pin hardware wire routing architectures
//
// Pin schema:
//   offsetStart / offsetEnd: magnifier SVG line + button position
//   offsetWire:  start offset when routing the cursor-following wire
//   targetPinKey: key to look up in the target port's _FULL.pins data
//                 (defaults to pinKey itself for power pins like + / -)
//   isSignal:    true for the one signal pin of the component
//   allowedTargets (signal-pin only): list of {portId, pinKey, label} pairs.
//     portId  — the _FULL port key in PortData (e.g. 'servo_motor1_FULL')
//     pinKey  — the key inside that port's `pins` object
//     label   — short label shown on the hover-hint circle (e.g. 'S1')
//   label: short text for the magnifier button (falls back to first char of pin key)
export const HardwareWireConfigs = {
    RGB: {
        flatLength: 80,
        imageSize: '110',
        imageX: '-94',
        imageY: '185',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10], // x,y
                offsetEnd: [-5, 30], // x,y  — button location
                offsetWire: [2, 4],
                targetPinKey: '+', // lookup key in the target port's _FULL.pins
            },
            '-': {
                stroke: '#333333',
                offsetStart: [5, -10],
                offsetEnd: [15, 30],
                offsetWire: [7, 4],
                targetPinKey: '-',
            },
            servo_motor1: {
                stroke: '#35a268',
                offsetStart: [-15, -10],
                offsetEnd: [-25, 30],
                offsetWire: [-3, 4],
                isSignal: true,
                label: 'S',
                allowedTargets: [
                    {
                        portId: 'servo_motor1_FULL',
                        pinKey: 'servo_motor1',
                        label: 'S1',
                    },
                ],
                allowedTargetsByDevice: {
                    Poki: [
                        { portId: 'F1_FULL', pinKey: 'F1', label: 'F1' },
                    ],
                },
            },
        },
    },
    // dynamex_motor: { ... }
    servo_motor_360: {
        flatLength: 80,
        imageSize: '110',
        imageX: '-274',
        imageY: '140',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10],
                offsetEnd: [-5, 30],
                offsetWire: [-10, -2],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [5, -10],
                offsetEnd: [15, 30],
                offsetWire: [-4, -2],
                targetPinKey: '-',
            },
            // Single signal pin that can connect to EITHER servo_motor1 OR servo_motor2.
            // The user picks one target; the component is "connected" once all 3 pins are wired.
            signal: {
                stroke: '#35a268',
                offsetStart: [-15, -10],
                offsetEnd: [-25, 30],
                offsetWire: [-16, -2],
                isSignal: true,
                label: 'S',
                allowedTargets: [
                    {
                        portId: 'servo_motor1_FULL',
                        pinKey: 'servo_motor1',
                        label: 'S1',
                    },
                    {
                        portId: 'servo_motor2_FULL',
                        pinKey: 'servo_motor2',
                        label: 'S2',
                    },
                ],
                allowedTargetsByDevice: {
                    Poki: [
                        {
                            portId: 'servo_motor1_FULL',
                            pinKey: 'servo_motor1',
                            label: 'S1',
                        },
                        {
                            portId: 'servo_motor2_FULL',
                            pinKey: 'servo_motor2',
                            label: 'S2',
                        },
                        { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                        { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                    ],
                },
            },
        },
    },
    servo_motor_270: {
        flatLength: 80,
        imageSize: '110',
        imageX: '-274',
        imageY: '140',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10],
                offsetEnd: [-5, 30],
                offsetWire: [-10, -2],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [5, -10],
                offsetEnd: [15, 30],
                offsetWire: [-4, -2],
                targetPinKey: '-',
            },
            // Single signal pin that can connect to EITHER servo_motor1 OR servo_motor2.
            // The user picks one target; the component is "connected" once all 3 pins are wired.
            signal: {
                stroke: '#35a268',
                offsetStart: [-15, -10],
                offsetEnd: [-25, 30],
                offsetWire: [-16, -2],
                isSignal: true,
                label: 'S',
                allowedTargets: [
                    {
                        portId: 'servo_motor1_FULL',
                        pinKey: 'servo_motor1',
                        label: 'S1',
                    },
                    {
                        portId: 'servo_motor2_FULL',
                        pinKey: 'servo_motor2',
                        label: 'S2',
                    },
                ],
                allowedTargetsByDevice: {
                    Poki: [
                        {
                            portId: 'servo_motor1_FULL',
                            pinKey: 'servo_motor1',
                            label: 'S1',
                        },
                        {
                            portId: 'servo_motor2_FULL',
                            pinKey: 'servo_motor2',
                            label: 'S2',
                        },
                        { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                        { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                    ],
                },
            },
        },
    },
    // Motor ports (M1/M3) carry TWO signal wires — M+ and M- — that land on the
    // port's two signal pins (M1+/M1- or M3+/M3-). M+ may only reach M1+ or M3+,
    // M- only M1- or M3-; each pin's allowedTargets enforce that. Gated to Toki and
    // Poki via `devices` (see getHardwareWireConfig): on those boards the motor uses
    // the magnifier + _FULL hint flow (just like the 3-pin servo ports). On every
    // other device it stays on its legacy single-wire branch with no hints.
    // NOTE: a hint LABEL comes from PORT_NAME_MAP keyed on the target's pinKey
    // (M3 → 'M2', so M3+ → 'M2+'), falling back to the allowedTargets `label`.
    dc_motor: {
        devices: ['Poki', 'Toki'],
        flatLength: 80,
        imageSize: '110',
        imageX: '0',
        imageY: '150',
        pins: {
            // The two motor wires. Both are "signal" pins (the component is
            // connected once BOTH are wired) and each carries its own allowedTargets
            // so M+ resolves to M1+/M3+ and M- to M1-/M3-. Same on Toki and Poki, so
            // the base allowedTargets are used (no per-device override needed).
            'M+': {
                stroke: '#ff4d4d',
                offsetStart: [-8, -10],
                offsetEnd: [-12, 30],
                offsetWire: [-1, -1],
                isSignal: true,
                label: ' ',
                allowedTargets: [
                    { portId: 'M1_FULL', pinKey: 'M1+', label: ' ' },
                    { portId: 'M1_FULL', pinKey: 'M1-', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3+', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3-', label: ' ' },
                ],
            },
            'M-': {
                stroke: '#333333',
                offsetStart: [8, -10],
                offsetEnd: [12, 30],
                offsetWire: [9, 2],
                isSignal: true,
                label: ' ',
                allowedTargets: [
                    { portId: 'M1_FULL', pinKey: 'M1-', label: ' ' },
                    { portId: 'M1_FULL', pinKey: 'M1+', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3-', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3+', label: ' ' },
                ],
            },
        },
    },
    dynamex_motor: {
        devices: ['Poki', 'Toki'],
        flatLength: 80,
        imageSize: '110',
        imageX: '-200',
        imageY: '-50',
        pins: {
            // Two motor wires (M+/M-) → M1+/M3+ and M1-/M3-. See dc_motor above.
            'M+': {
                stroke: '#ff4d4d',
                offsetStart: [-8, -10],
                offsetEnd: [-12, 30],
                offsetWire: [-16, -22],
                isSignal: true,
                label: ' ',
                allowedTargets: [
                    { portId: 'M1_FULL', pinKey: 'M1+', label: ' ' },
                    { portId: 'M1_FULL', pinKey: 'M1-', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3+', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3-', label: ' ' },
                ],
            },
            'M-': {
                stroke: '#333333',
                offsetStart: [8, -10],
                offsetEnd: [12, 30],
                offsetWire: [-12, -22],
                isSignal: true,
                label: ' ',
                allowedTargets: [
                    { portId: 'M1_FULL', pinKey: 'M1-', label: ' ' },
                    { portId: 'M1_FULL', pinKey: 'M1+', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3-', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3+', label: ' ' },
                ],
            },
        },
    },
    mini_geared_motor: {
        devices: ['Poki', 'Toki'],
        flatLength: 80,
        imageSize: '110',
        imageX: '-280',
        imageY: '140',
        pins: {
            // Two motor wires (M+/M-) → M1+/M3+ and M1-/M3-. See dc_motor above.
            'M+': {
                stroke: '#ff4d4d',
                offsetStart: [-8, -10],
                offsetEnd: [-12, 30],
                offsetWire: [-6, -3],
                isSignal: true,
                label: ' ',
                allowedTargets: [
                    { portId: 'M1_FULL', pinKey: 'M1+', label: ' ' },
                    { portId: 'M1_FULL', pinKey: 'M1-', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3+', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3-', label: ' ' },
                ],
            },
            'M-': {
                stroke: '#333333',
                offsetStart: [8, -10],
                offsetEnd: [12, 30],
                offsetWire: [0, -3],
                isSignal: true,
                label: ' ',
                allowedTargets: [
                    { portId: 'M1_FULL', pinKey: 'M1-', label: ' ' },
                    { portId: 'M1_FULL', pinKey: 'M1+', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3-', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3+', label: ' ' },
                ],
            },
        },
    },
    geared_motor: {
        devices: ['Poki', 'Toki'],
        flatLength: 80,
        imageSize: '110',
        imageX: '-170',
        imageY: '140',
        pins: {
            // Two motor wires (M+/M-) → M1+/M3+ and M1-/M3-. See dc_motor above.
            'M+': {
                stroke: '#ff4d4d',
                offsetStart: [-8, -10],
                offsetEnd: [-12, 30],
                offsetWire: [-5, -2],
                isSignal: true,
                label: ' ',
                allowedTargets: [
                    { portId: 'M1_FULL', pinKey: 'M1+', label: ' ' },
                    { portId: 'M1_FULL', pinKey: 'M1-', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3+', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3-', label: ' ' },
                ],
            },
            'M-': {
                stroke: '#333333',
                offsetStart: [8, -10],
                offsetEnd: [12, 30],
                offsetWire: [0, 0],
                isSignal: true,
                label: ' ',
                allowedTargets: [
                    { portId: 'M1_FULL', pinKey: 'M1-', label: ' ' },
                    { portId: 'M1_FULL', pinKey: 'M1+', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3-', label: ' ' },
                    { portId: 'M3_FULL', pinKey: 'M3+', label: ' ' },
                ],
            },
        },
    },
    servo_motor: {
        flatLength: 80,
        imageSize: '110',
        imageX: '-274',
        imageY: '140',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10],
                offsetEnd: [-5, 30],
                offsetWire: [-10, -2],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [5, -10],
                offsetEnd: [15, 30],
                offsetWire: [-4, -2],
                targetPinKey: '-',
            },
            // Single signal pin that can connect to EITHER servo_motor1 OR servo_motor2.
            // The user picks one target; the component is "connected" once all 3 pins are wired.
            signal: {
                stroke: '#35a268',
                offsetStart: [-15, -10],
                offsetEnd: [-25, 30],
                offsetWire: [-16, -2],
                isSignal: true,
                label: 'S',
                // Base targets (Toki and anything without a device override).
                allowedTargets: [
                    {
                        portId: 'servo_motor1_FULL',
                        pinKey: 'servo_motor1',
                        label: 'S1',
                    },
                    {
                        portId: 'servo_motor2_FULL',
                        pinKey: 'servo_motor2',
                        label: 'S2',
                    },
                ],
                // Per-device override (see getAllowedTargets). On Poki a servo can
                // also be wired to the A1/A2 motor ports.
                // NOTE: servo_motor1_FULL, servo_motor2_FULL and A1_FULL have Poki
                // port data (coords + lines) in PortData.js. The A2 entry below stays
                // inert until A2_FULL is added to the Poki block there.
                allowedTargetsByDevice: {
                    Poki: [
                        {
                            portId: 'servo_motor1_FULL',
                            pinKey: 'servo_motor1',
                            label: 'S1',
                        },
                        {
                            portId: 'servo_motor2_FULL',
                            pinKey: 'servo_motor2',
                            label: 'S2',
                        },
                        { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                        { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                    ],
                },
            },
        },
    },
    servo_motor_sg90: {
        flatLength: 80,
        imageSize: '110',
        imageX: '280',
        imageY: '140',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10],
                offsetEnd: [-5, 30],
                offsetWire: [24, -2],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [5, -20],
                offsetEnd: [15, 30],
                offsetWire: [30, -6],
                targetPinKey: '-',
            },
            // Single signal pin that can connect to EITHER servo_motor1 OR servo_motor2.
            // The user picks one target; the component is "connected" once all 3 pins are wired.
            signal: {
                stroke: '#35a268',
                offsetStart: [-15, 0],
                offsetEnd: [-25, 30],
                offsetWire: [18, 2],
                isSignal: true,
                label: 'S',
                allowedTargets: [
                    {
                        portId: 'servo_motor1_FULL',
                        pinKey: 'servo_motor1',
                        label: 'S1',
                    },
                    {
                        portId: 'servo_motor2_FULL',
                        pinKey: 'servo_motor2',
                        label: 'S2',
                    },
                ],
                allowedTargetsByDevice: {
                    Poki: [
                        {
                            portId: 'servo_motor1_FULL',
                            pinKey: 'servo_motor1',
                            label: 'S1',
                        },
                        {
                            portId: 'servo_motor2_FULL',
                            pinKey: 'servo_motor2',
                            label: 'S2',
                        },
                        { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                        { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                    ],
                },
            },
        },
    },
    Analog_3_Pin_Input: {
        flatLength: 80,
        imageSize: '110',
        imageX: '10',
        imageY: '165',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10],
                offsetEnd: [-5, 30],
                offsetWire: [6, 0],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [15, -10],
                offsetEnd: [25, 30],
                offsetWire: [14, 0],
                targetPinKey: '-',
            },
            // Single signal pin that can connect to EITHER servo_motor1 OR servo_motor2.
            // The user picks one target; the component is "connected" once all 3 pins are wired.
            signal: {
                stroke: '#35a268',
                offsetStart: [-25, -10],
                offsetEnd: [-35, 30],
                offsetWire: [-2, 0],
                isSignal: true,
                label: 'S',
                allowedTargets: [
                    { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                    { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                ],
                allowedTargetsByDevice: {
                    Poki: [
                        { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                        { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                        { portId: 'B1_FULL', pinKey: 'B1', label: 'B1' },
                        { portId: 'C1_FULL', pinKey: 'C1', label: 'C1' },
                        { portId: 'C2_FULL', pinKey: 'C2', label: 'C2' },
                        { portId: 'D2_FULL', pinKey: 'D2', label: 'D2' },
                        { portId: 'F1_FULL', pinKey: 'F1', label: 'F1' },
                        { portId: 'F2_FULL', pinKey: 'F2', label: 'F2' },
                        {
                            portId: 'servo_motor1_FULL',
                            pinKey: 'servo_motor1',
                            label: 'S1',
                        },
                    ],
                },
            },
        },
    },
    Digital_3_Pin_Input: {
        flatLength: 80,
        imageSize: '110',
        imageX: '10',
        imageY: '165',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10],
                offsetEnd: [-5, 30],
                offsetWire: [6, 0],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [15, -10],
                offsetEnd: [25, 30],
                offsetWire: [14, 0],
                targetPinKey: '-',
            },
            // Single signal pin that can connect to EITHER servo_motor1 OR servo_motor2.
            // The user picks one target; the component is "connected" once all 3 pins are wired.
            signal: {
                stroke: '#35a268',
                offsetStart: [-25, -10],
                offsetEnd: [-35, 30],
                offsetWire: [-2, 0],
                isSignal: true,
                label: 'S',
                allowedTargets: [
                    { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                    { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                    {
                        portId: 'servo_motor1_FULL',
                        pinKey: 'servo_motor1',
                        label: 'S1',
                    },
                    {
                        portId: 'servo_motor2_FULL',
                        pinKey: 'servo_motor2',
                        label: 'S2',
                    },
                ],
                allowedTargetsByDevice: {
                    Poki: [
                        { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                        { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                        { portId: 'B1_FULL', pinKey: 'B1', label: 'B1' },
                        { portId: 'B2_FULL', pinKey: 'B2', label: 'B2' },
                        { portId: 'C1_FULL', pinKey: 'C1', label: 'C1' },
                        { portId: 'C2_FULL', pinKey: 'C2', label: 'C2' },
                        { portId: 'D1_FULL', pinKey: 'D1', label: 'D1' },
                        { portId: 'D2_FULL', pinKey: 'D2', label: 'D2' },
                        { portId: 'E1_FULL', pinKey: 'E1', label: 'E1' },
                        { portId: 'E2_FULL', pinKey: 'E2', label: 'E2' },
                        { portId: 'F1_FULL', pinKey: 'F1', label: 'F1' },
                        { portId: 'F2_FULL', pinKey: 'F2', label: 'F2' },
                        {
                            portId: 'servo_motor1_FULL',
                            pinKey: 'servo_motor1',
                            label: 'S1',
                        },
                        {
                            portId: 'servo_motor2_FULL',
                            pinKey: 'servo_motor2',
                            label: 'S2',
                        },
                    ],
                },
            },
        },
    },
    PWM_3_Pin_Output: {
        flatLength: 80,
        imageSize: '110',
        imageX: '10',
        imageY: '165',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10],
                offsetEnd: [-5, 30],
                offsetWire: [6, 0],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [15, -10],
                offsetEnd: [25, 30],
                offsetWire: [14, 0],
                targetPinKey: '-',
            },
            // Single signal pin that can connect to EITHER servo_motor1 OR servo_motor2.
            // The user picks one target; the component is "connected" once all 3 pins are wired.
            signal: {
                stroke: '#35a268',
                offsetStart: [-25, -10],
                offsetEnd: [-35, 30],
                offsetWire: [-2, 0],
                isSignal: true,
                label: 'S',
                allowedTargets: [
                    {
                        portId: 'servo_motor1_FULL',
                        pinKey: 'servo_motor1',
                        label: 'S1',
                    },
                    {
                        portId: 'servo_motor2_FULL',
                        pinKey: 'servo_motor2',
                        label: 'S2',
                    },
                ],
                allowedTargetsByDevice: {
                    Poki: [
                        { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                        { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                        {
                            portId: 'servo_motor1_FULL',
                            pinKey: 'servo_motor1',
                            label: 'S1',
                        },
                        {
                            portId: 'servo_motor2_FULL',
                            pinKey: 'servo_motor2',
                            label: 'S2',
                        },
                    ],
                },
            },
        },
    },
    Digital_3_Pin_Output: {
        flatLength: 80,
        imageSize: '110',
        imageX: '10',
        imageY: '165',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10],
                offsetEnd: [-5, 30],
                offsetWire: [6, 0],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [15, -10],
                offsetEnd: [25, 30],
                offsetWire: [14, 0],
                targetPinKey: '-',
            },
            // Single signal pin that can connect to EITHER servo_motor1 OR servo_motor2.
            // The user picks one target; the component is "connected" once all 3 pins are wired.
            signal: {
                stroke: '#35a268',
                offsetStart: [-25, -10],
                offsetEnd: [-35, 30],
                offsetWire: [-2, 0],
                isSignal: true,
                label: 'S',
                allowedTargets: [
                    { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                    { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                    {
                        portId: 'servo_motor1_FULL',
                        pinKey: 'servo_motor1',
                        label: 'S1',
                    },
                    {
                        portId: 'servo_motor2_FULL',
                        pinKey: 'servo_motor2',
                        label: 'S2',
                    },
                ],
                allowedTargetsByDevice: {
                    Poki: [
                        {
                            portId: 'servo_motor1_FULL',
                            pinKey: 'servo_motor1',
                            label: 'S1',
                        },
                        {
                            portId: 'servo_motor2_FULL',
                            pinKey: 'servo_motor2',
                            label: 'S2',
                        },
                        { portId: 'A1_FULL', pinKey: 'A1', label: 'A1' },
                        { portId: 'A2_FULL', pinKey: 'A2', label: 'A2' },
                        { portId: 'C1_FULL', pinKey: 'C1', label: 'C1' },
                        { portId: 'C2_FULL', pinKey: 'C2', label: 'C2' },
                        { portId: 'B1_FULL', pinKey: 'B1', label: 'B1' },
                        { portId: 'B2_FULL', pinKey: 'B2', label: 'B2' },
                        { portId: 'D1_FULL', pinKey: 'D1', label: 'D1' },
                        { portId: 'D2_FULL', pinKey: 'D2', label: 'D2' },
                        { portId: 'E1_FULL', pinKey: 'E1', label: 'E1' },
                        { portId: 'E2_FULL', pinKey: 'E2', label: 'E2' },
                        { portId: 'F1_FULL', pinKey: 'F1', label: 'F1' },
                        { portId: 'F2_FULL', pinKey: 'F2', label: 'F2' },
                    ],
                },
            },
        },
    },

    ultrasonic_sensor: {
        flatLength: 80,
        imageSize: '110',
        imageX: '0',
        imageY: '-100',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-5, -10],
                offsetEnd: [-5, 30],
                offsetWire: [2, -12],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [5, -10],
                offsetEnd: [15, 30],
                offsetWire: [8, -12],
                targetPinKey: '-',
            },
            signal: {
                stroke: '#35a268',
                offsetStart: [-15, -10],
                offsetEnd: [-25, 30],
                offsetWire: [-4, -12],
                isSignal: true,
                label: 'S',
                allowedTargets: [
                    {
                        portId: 'servo_motor1_FULL',
                        pinKey: 'servo_motor1',
                        label: 'S1',
                    },
                    {
                        portId: 'servo_motor2_FULL',
                        pinKey: 'servo_motor2',
                        label: 'S2',
                    },
                ],
            },
        },
    },

    // ── Poki-exclusive I2C components ──────────────────────────────────────────
    // These plug into the single E socket (and E1 for some) with ONE connector, so
    // they're modelled like dc_motor: a single signal pin, gated to Poki via
    // `devices`. On Poki they get the magnifier + the E-group blob hint (E/E1/E2);
    // the wire attaches at whichever alias is wired (E or E1). The pin's label comes
    // from the target's pinKey, so the hint reads 'E' / 'E1'. Allowed board ports
    // mirror AllowedPortTypes.POKI1. Not present on Toki — they stay legacy there.
    IMU: {
        devices: ['Poki'],
        flatLength: 80,
        imageSize: '110',
        imageX: '-60',
        imageY: '185',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-26, -14],
                offsetEnd: [-40, 30],
                offsetWire: [-7, 10],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [10, -10],
                offsetEnd: [20, 30],
                offsetWire: [8, 10],
                targetPinKey: '-',
            },
            E1: {
                stroke: '#35a268',
                offsetStart: [-12, -10],
                offsetEnd: [-20, 30],
                offsetWire: [-2, 10],
                isSignal: true,
                label: 'E1',
                allowedTargets: [],
                allowedTargetsByDevice: {
                    Poki: [
                        { portId: 'E1_FULL', pinKey: 'E1', label: 'E1' },
                    ],
                },
            },
            E2: {
                stroke: '#35a268',
                offsetStart: [0, -10],
                offsetEnd: [0, 30],
                offsetWire: [3, 10],
                isSignal: true,
                label: 'E2',
                allowedTargets: [],
                allowedTargetsByDevice: {
                    Poki: [
                        { portId: 'E2_FULL', pinKey: 'E2', label: 'E2' },
                    ],
                },
            },
        },
    },
    OLED: {
        devices: ['Poki'],
        flatLength: 80,
        imageSize: '110',
        imageX: '485',
        imageY: '-80',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [0, -32],
                offsetEnd: [56, -6],
                offsetWire: [54, -26],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [-5, -10],
                offsetEnd: [0, 30],
                offsetWire: [48, -14],
                targetPinKey: '-',
            },
            E1: {
                stroke: '#35a268',
                offsetStart: [-1, -24],
                offsetEnd: [40, 10],
                offsetWire: [52, -22],
                isSignal: true,
                label: 'E1',
                allowedTargets: [],
                allowedTargetsByDevice: {
                    Poki: [{ portId: 'E1_FULL', pinKey: 'E1', label: 'E1' }],
                },
            },
            E2: {
                stroke: '#35a268',
                offsetStart: [-2, -15],
                offsetEnd: [20, 20],
                offsetWire: [50, -18],
                isSignal: true,
                label: 'E2',
                allowedTargets: [],
                allowedTargetsByDevice: {
                    Poki: [{ portId: 'E2_FULL', pinKey: 'E2', label: 'E2' }],
                },
            },
        },
    },
    Color_sensor: {
        devices: ['Poki'],
        flatLength: 80,
        imageSize: '110',
        imageX: '-110',
        imageY: '185',
        pins: {
            '+': {
                stroke: '#ff4d4d',
                offsetStart: [-26, -14],
                offsetEnd: [-40, 30],
                offsetWire: [-7, 10],
                targetPinKey: '+',
            },
            '-': {
                stroke: '#333333',
                offsetStart: [10, -10],
                offsetEnd: [20, 30],
                offsetWire: [8, 10],
                targetPinKey: '-',
            },
            E1: {
                stroke: '#35a268',
                offsetStart: [-12, -10],
                offsetEnd: [-20, 30],
                offsetWire: [-2, 10],
                isSignal: true,
                label: 'E1',
                allowedTargets: [],
                allowedTargetsByDevice: {
                    Poki: [
                        { portId: 'E1_FULL', pinKey: 'E1', label: 'E1' },
                    ],
                },
            },
            E2: {
                stroke: '#35a268',
                offsetStart: [0, -10],
                offsetEnd: [0, 30],
                offsetWire: [3, 10],
                isSignal: true,
                label: 'E2',
                allowedTargets: [],
                allowedTargetsByDevice: {
                    Poki: [
                        { portId: 'E2_FULL', pinKey: 'E2', label: 'E2' },
                    ],
                },
            },
        },
    },
}

// Signal pins may reach different board ports depending on the connected device
// (e.g. a servo targets S1/S2 on Toki but also A1/A2 on Poki). A pin config can
// declare an `allowedTargetsByDevice` map to override the base `allowedTargets`
// for specific devices; otherwise the base list is used. Always read a pin's
// targets through this helper so every consumer stays device-consistent.
export const getAllowedTargets = (pinMeta, device) => {
    // woofKit is Poki-based: reuse Poki's per-device target overrides.
    if (device === 'woofKit') device = 'Poki'
    return (
        (pinMeta &&
            (pinMeta.allowedTargetsByDevice?.[device] ||
                pinMeta.allowedTargets)) ||
        undefined
    )
}

// Some components (e.g. dc_motor) should only use the magnifier + multi-pin hint
// flow on specific devices, staying on their legacy single-wire branch elsewhere.
// A config may declare a `devices` allow-list to opt into that gating; configs
// WITHOUT a `devices` field are available everywhere (unchanged behaviour).
//
// Read a config through this helper anywhere the result decides which rendering
// path a component takes, so a device-restricted config is invisible on the
// devices it doesn't support (returns undefined → falls through to legacy).
export const getHardwareWireConfig = (type, device) => {
    // woofKit is Poki-based: opt it into every Poki-gated multi-pin config.
    if (device === 'woofKit') device = 'Poki'
    const config = HardwareWireConfigs[type]
    if (!config) return undefined
    if (config.devices && !config.devices.includes(device)) return undefined
    return config
}

// A "whole-socket" component has 2+ signal pins that each target a SINGLE alias, and
// those aliases all belong to the SAME grouped socket (e.g. OLED's E1 + E2 → the one
// physical E socket). Such a component occupies the whole group, so it connects
// logically to the group PARENT (e.g. 'E') — matching an auto-drop onto E — and its
// routing hint shows the full group blob rather than a lone alias circle. Components
// with a single signal pin that merely PICKS one of several group aliases (e.g. a
// Digital output choosing F1 or F2) are NOT whole-socket and keep their chosen alias.
//   `getGroup` is PortGroups.getPortGroup, passed in to avoid an import cycle.
// Returns the group's parent alias, or undefined for non-whole-socket components.
export const getWholeSocketParent = (type, device, getGroup) => {
    const cfg = getHardwareWireConfig(type, device)
    if (!cfg) return undefined
    const signalPins = Object.keys(cfg.pins).filter((k) => cfg.pins[k].isSignal)
    if (signalPins.length < 2) return undefined
    const targets = []
    for (const k of signalPins) {
        const t = getAllowedTargets(cfg.pins[k], device)
        if (!t || t.length !== 1) return undefined
        targets.push(t[0].pinKey)
    }
    const group = getGroup(device, targets[0])
    if (group.length < 2 || !targets.every((p) => group.includes(p)))
        return undefined
    return group[0]
}

// ─── Auto-drop power-pin routing ────────────────────────────────────────────
// When a multi-pin component is dropped on a port, its + / - wires need a board
// pin to land on. A port may not own + / - at all (e.g. Poki D1_FULL is signal
// only), or may own them but already have them taken by another component. The
// + and - are routed INDEPENDENTLY: one can land on a port's own pin while the
// other spills elsewhere. This logic is shared by the drop handler (workspace.js)
// AND the live drag preview (Connections.js) so what you see while hovering is
// exactly what you get on release.

// Ordered fallback pool of board ports that own + / - power pins, tried (after
// the dropped port itself) when the dropped port's own + / - is missing or taken.
// The dedicated `fallbackPins` rail comes first (no competing signal use), then
// the second dedicated `fallbackPins2` rail when the first is taken; the real
// port pins (A1/A2/servo) are overflow. Each entry holds one + and one -.
export const POWER_PIN_PORTS = [
    'fallbackPins',
    'fallbackPins2',
    'A1',
    'A2',
    'servo_motor1',
    'servo_motor2',
]

// The full port id holding a port's pin coordinates. The fallbackPins rails are
// special: they have no `_FULL` variant (see PortData.js).
const fullPortId = (portName) =>
    portName === 'fallbackPins' || portName === 'fallbackPins2'
        ? portName
        : portName + '_FULL'

// Does this board port own a dedicated pin for `pinKey` ('+' or '-')?
// `getPort` is PortData's default export (passed in to avoid a circular import).
const portHasPin = (getPort, portName, pinKey) =>
    !!getPort(fullPortId(portName))?.pins?.[pinKey]

// Which power slot does a stored wire value occupy for `pinKey`? A value that
// points at a port lacking its own + / - collapses onto the shared fallbackPins
// slot — mirroring how getFallbackPortData renders it in Connections.js.
const resolvePowerSlot = (getPort, portName, pinKey) =>
    portName && portHasPin(getPort, portName, pinKey) ? portName : 'fallbackPins'

/**
 * Build the per-pin wiring for a component auto-dropped on `droppedPort`.
 * Signal pins wire straight to droppedPort. Each power pin (+ / -) prefers the
 * dropped port's own pin when free, otherwise scans POWER_PIN_PORTS for the
 * first free slot; + and - are resolved independently. If a power pin has no
 * free slot it stays null and `allConnected` becomes false.
 *
 * @param  {function} getPort PortData's default export (Ports)
 * @param  {string} type The component type
 * @param  {string} droppedPort The port the component was dropped on
 * @param  {string} device The connected device
 * @param  {object} components The workspace components map (for occupancy)
 * @param  {?{type:string,index:number}} skip Component to exclude (the one being placed)
 * @return {?{pinConnections:object, allConnected:boolean}}
 */
export const computeAutoDropPinConnections = (
    getPort,
    type,
    droppedPort,
    device,
    components,
    skip
) => {
    const cfg = getHardwareWireConfig(type, device)
    if (!cfg) return null

    // Slots already taken across every OTHER component, per power pin key.
    const occupied = { '+': new Set(), '-': new Set() }
    Object.keys(components || {}).forEach((ct) => {
        ;(components[ct] || []).forEach((comp, ci) => {
            if (skip && ct === skip.type && ci === skip.index) return
            const pc = comp.pinConnections
            ;['+', '-'].forEach((pk) => {
                let v = pc ? pc[pk] : undefined
                // Legacy/restored components carry only `connectedTo`: every pin
                // is wired to that port (same reconstruction the renderer and
                // magnifier use). Mirror it so their power wires reserve a slot.
                if (
                    v === undefined &&
                    (!pc || Object.keys(pc).length === 0) &&
                    comp.connectedTo
                ) {
                    v = comp.connectedTo
                }
                if (v) occupied[pk].add(resolvePowerSlot(getPort, v, pk))
            })
        })
    })

    const pinConnections = {}
    let allConnected = true
    Object.keys(cfg.pins).forEach((pk) => {
        if (pk !== '+' && pk !== '-') {
            pinConnections[pk] = droppedPort
            return
        }
        // Prefer the dropped port's own power pin, then the fallback pool.
        const candidates = [droppedPort, ...POWER_PIN_PORTS]
        const free = candidates.find(
            (p) =>
                portHasPin(getPort, p, pk) &&
                !occupied[pk].has(resolvePowerSlot(getPort, p, pk))
        )
        if (free) {
            pinConnections[pk] = free
            occupied[pk].add(resolvePowerSlot(getPort, free, pk))
        } else {
            pinConnections[pk] = null
            allConnected = false
        }
    })

    return { pinConnections, allConnected }
}
// PinMaps.js
// Source of truth for the joystick_v2 magnifier + per-wire routing feature (Roboki).
//
// joystick_v2 is a two-connector component:
//   • wire "A" -> board port A   (analog X = A1, Y = A2)
//   • wire "B" -> board port B   (button = B1)
// The two wires are independent: connecting A alone or B alone each counts.
//
// Geometry note: unlike the reference project this repo resolves a target port's
// screen position through the existing Ports(port) + bibox math (see PortData.js /
// Connections.js), so no `_FULL` port data is introduced here. The offsets below
// only position the magnifier's SVG lines + buttons and the fixed origin of the
// cursor-following wire; they are tuned visually.
//
// Per-wire schema:
//   stroke       wire colour (magnifier line + routed wire)
//   label        text shown on the magnifier button
//   offsetStart  [x,y] start of the magnifier <line> relative to the loupe centre
//   offsetEnd    [x,y] end of the magnifier <line> = button centre (relative to centre)
//   offsetWire   [x,y] fixed origin of the cursor-following wire, relative to the
//                component's top-left screen position
//   target.port  the board port this wire connects to
//   target.occupies  the PortConnections sub-pins claimed when this wire is wired

const resolveJoystickTargets = (device, connectedTo) => {
    if (device === 'Roboki') {
        return {
            A: [{ port: 'A', occupies: ['A1', 'A2'] }],
            B: [{ port: 'B', occupies: ['B1'] }],
        }
    }
    if (device === 'Hexapod') {
        return {
            A: [{ port: 'C', occupies: ['C1', 'C2'] }],
            B: [{ port: 'A', occupies: ['A1'] }],
        }
    }
    if (device === 'Humanoid') {
        const isV3 = sessionStorage.getItem('deviceVersion')?.[0] === '3'
        return isV3
            ? {
                  A: [{ port: 'B', occupies: ['B1', 'B2'] }],
                  B: [{ port: 'C', occupies: ['C1'] }],
              }
            : {
                  A: [{ port: 'A', occupies: ['A1', 'A2'] }],
                  B: [{ port: 'C', occupies: ['C1'] }],
              }
    }
    if (device === 'Ace') {
        const groupA = {
            A: { port: 'B', occupies: ['B1', 'B2'] },
            B: { port: 'A', occupies: ['A1'] },
        }
        const groupC = {
            A: { port: 'D', occupies: ['D1', 'D2'] },
            B: { port: 'C', occupies: ['C1'] },
        }
        const [first, second] =
            connectedTo === 'C' ? [groupC, groupA] : [groupA, groupC]
        return {
            A: [first.A, second.A],
            B: [first.B, second.B],
        }
    }
    if (device === 'Klaw') {
        const groupA = {
            A: { port: 'A', occupies: ['A1', 'A2'] },
            B: { port: 'B', occupies: ['B1'] },
        }
        const groupB = {
            A: { port: 'B', occupies: ['B1', 'B2'] },
            B: { port: 'A', occupies: ['A1'] },
        }
        const [first, second] =
            connectedTo === 'B' ? [groupB, groupA] : [groupA, groupB]
        return {
            A: [first.A, second.A],
            B: [first.B, second.B],
        }
    }
    return null
}


export const resolveConnectedTarget = (wire, connectedPort) =>
    (connectedPort && wire.targets.find((t) => t.port === connectedPort)) ||
    wire.targets[0]

export const JoystickWireConfig = {
    joystick_v2: {
        devices: ['Roboki', 'Ace', 'Hexapod', 'Humanoid', 'Klaw'],
        // Magnifier framing: a 150x150 circular lens with the component zoomed in.
        magnifier: {
            size: 160, // px diameter of the circular loupe
            zoom: 1.5, // background-size multiplier of the component image
            bgX: '50%', // background-position x
            bgY: '500%', // background-position y
        },
        wireStyle: {
            A: {
                stroke: '#ff4d4d',
                label: 'X/Y',
                offsetStart: [-26, 6],
                offsetEnd: [-34, 42],
                offsetWire: [32, 86],
            },
            B: {
                stroke: '#ff4d4d',
                label: 'SW',
                offsetStart: [16, 10],
                offsetEnd: [24, 42],
                offsetWire: [50, 84],
            },
        },
    },
    // Zing/Humanoid v0.1 and v1.0 only (pre-v2 hardware). Same 2-connector
    // layout as joystick_v2's Humanoid config (axes pair + button), confirmed
    // against HUMANOID1's AllowedPortTypes entry. It's never offered on v3
    // hardware (no v3 AllowedPortTypes entry for plain `joystick`), so
    // resolveJoystickTargets' non-v3 branch (A1/A2 axes, C1 button) always
    // applies here - no separate resolver needed.
    joystick: {
        devices: ['Humanoid'],
        magnifier: {
            size: 160,
            zoom: 1.5,
            bgX: '50%',
            bgY: '500%',
        },
        wireStyle: {
            A: {
                stroke: '#ff4d4d',
                label: 'A',
                offsetStart: [-26, 6],
                offsetEnd: [-34, 42],
                offsetWire: [32, 86],
            },
            B: {
                stroke: '#ff4d4d',
                label: 'B',
                offsetStart: [16, 10],
                offsetEnd: [24, 42],
                offsetWire: [50, 84],
            },
        },
    },
}

export const isJoystickWireType = (type) => !!JoystickWireConfig[type]

// Device-gated lookup. Returns undefined for components/devices that are not part
// of this feature, so every consumer stays on its legacy path unless opted in.
// `connectedTo` (the port the component is/was dropped on) picks between a
// device's alternate port groups where it has more than one (e.g. Ace A vs C).
export const getJoystickWireConfig = (type, device, connectedTo) => {
    const config = JoystickWireConfig[type]
    if (!config) return undefined
    if (config.devices && !config.devices.includes(device)) return undefined
    const targets = resolveJoystickTargets(device, connectedTo)
    if (!targets) return undefined
    return {
        magnifier: config.magnifier,
        wires: {
            A: { ...config.wireStyle.A, targets: targets.A },
            B: { ...config.wireStyle.B, targets: targets.B },
        },
    }
}

// Reverse map: given a board port, which wire key of `type` targets it (or null).
export const wireKeyForPort = (type, device, port, connectedTo) => {
    const config = getJoystickWireConfig(type, device, connectedTo)
    if (!config) return null
    return (
        Object.keys(config.wires).find((k) =>
            config.wires[k].targets.some((t) => t.port === port)
        ) || null
    )
}

// Decide the wireConnections a joystick_v2 should have after the WHOLE component is
// drag-dropped onto a valid port. Rules:
//   • if at least one wire is currently connected -> preserve exactly those wires
//     (so re-dragging a partially-wired joystick keeps its per-wire state, even
//     if it's connected to the "other" candidate port, e.g. Ace's C group);
//   • if none are connected (fresh drop, or after a full manual disconnect) ->
//     auto-connect each wire to its first free candidate target, skipping any
//     port already occupied by another component (partial auto-connect).
// `portConnections` is the current redux PortConnections (with this joystick's own
// pins already released during drag); omit it to treat every port as free.
// `device`/`droppedPort` pick which candidate is tried first (see resolveJoystickTargets).
export const computeJoystickDropWires = (
    wireConnections,
    portConnections,
    device,
    droppedPort
) => {
    const wc = wireConnections
    const anyConnected = !!(wc && (wc.A || wc.B))
    if (anyConnected) {
        return { A: wc.A || null, B: wc.B || null }
    }
    const cfg = getJoystickWireConfig('joystick_v2', device, droppedPort)
    if (!cfg) return { A: null, B: null }
    const pickFreeTarget = (wireKey) => {
        const free = cfg.wires[wireKey].targets.find(
            (t) =>
                !portConnections ||
                t.occupies.every((pin) => portConnections[pin] == null)
        )
        return free ? free.port : null
    }
    return { A: pickFreeTarget('A'), B: pickFreeTarget('B') }
}
